// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  IsoCurrencyCode,
  ZERO_DECIMAL_CURRENCIES,
} from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { MAX_DOLLAR_VALUE } from '@/utilities/currency';
import { Decimal } from 'decimal.js';

/** Largest absolute amount supported by the BE NUMERIC(14,2) money columns. */
const MAX_CURRENCY_AMOUNT = new CurrencyDecimal(MAX_DOLLAR_VALUE);

// Matches the BE decimal parser's accept-set (plain and scientific decimal
// notation). decimal.js alone is wider — hex/binary/octal/underscore literals
// and NaN/Infinity parse there but are rejected by the BE with 3807.
const PLAIN_DECIMAL_PATTERN = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;

/** Parses a decimal amount string; null when it is not a plain finite number. */
export function parseCurrencyAmount(value: string): Decimal | null {
  if (!PLAIN_DECIMAL_PATTERN.test(value)) {
    return null;
  }
  const amount = new CurrencyDecimal(value);
  return amount.isFinite() ? amount : null;
}

/** Mirrors `currency.ExceedsMaxDollarAmount` on the BE: exact check, no rounding first. */
export function exceedsMaxCurrencyAmount(amount: Decimal): boolean {
  return amount.abs().greaterThan(MAX_CURRENCY_AMOUNT);
}

/** Mirrors `currency.HasSubCentPrecision` on the BE: a non-zero component below cents. */
function hasSubCentPrecision(amount: Decimal): boolean {
  const cents = amount.times(100);
  return !cents.equals(cents.truncated());
}

/** Whether an amount has a non-zero component below whole units. */
export function hasFractionalUnits(amount: Decimal): boolean {
  return !amount.equals(amount.truncated());
}

/** Whether an ISO 4217 code is a whole-unit currency (JPY, KRW, VND). */
export function isZeroDecimal(currencyCode: IsoCurrencyCode): boolean {
  return ZERO_DECIMAL_CURRENCIES.has(currencyCode);
}

/** Returns the number of decimal places in a currency's minor unit. */
export function getMinorUnitDigits(currencyCode: IsoCurrencyCode): number {
  return isZeroDecimal(currencyCode) ? 0 : 2;
}

/** Returns why an amount is finer than its currency's minor unit, or null. */
export function getAmountPrecisionMessage(
  amount: Decimal,
  currencyCode: IsoCurrencyCode
): string | null {
  if (hasSubCentPrecision(amount)) {
    return 'Amount must not include more than 2 decimal places';
  }

  if (isZeroDecimal(currencyCode) && hasFractionalUnits(amount)) {
    return `${currencyCode} amounts must be whole units`;
  }

  return null;
}
